import debounce from 'lodash.debounce';
import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import ImprovedSelect from './ImprovedSelect';
import PatientModal from '../../Modal/PatientModal';
import PatientImage from '../../Patient/PatientImage';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserPlus } from '@fortawesome/free-solid-svg-icons';
import { useFormContext } from 'react-hook-form';
import { API_BASE_URL } from '../../../api';

const InputPatient = ({
  name,
  handleChange,
  validationRules = {},
  allowCustomValue = false,
  className,
  clearOnMount = false,
}) => {
  const [options, setOptions] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [currentKeyword, setCurrentKeyword] = useState('');
  const inputRef = useRef(null);
  const { getValues } = useFormContext();

  const keywordRef = useRef('');
  const pageRef = useRef(0);
  const loadingRef = useRef(false);
  const hasMoreRef = useRef(false);
  const pinnedPatientRef = useRef(null); // always kept in options even after re-fetch

  const fetchPatients = useCallback(async (keyword, pageNum, append) => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/api/v1/getPatients`, {
        withCredentials: true,
        params: { page: pageNum, size: 20, keyword: keyword || undefined },
      });
      const data = response.data;
      const patients = (data.content || []).map((p) => ({
        value: p.id,
        label: p.fullName,
        icon: <PatientImage patient={p} size={24} />,
      }));
      const more = !data.last;
      hasMoreRef.current = more;
      setHasMore(more);
      setOptions(prev => {
        const base = append ? prev : [];
        const merged = [...base];
        for (const p of patients) {
          if (!merged.some(m => m.value === p.value)) merged.push(p);
        }
        // Always keep the pinned (selected) patient in the list
        const pinned = pinnedPatientRef.current;
        if (pinned && !merged.some(m => m.value === pinned.value)) {
          merged.unshift(pinned);
        }
        return merged;
      });
      pageRef.current = pageNum;
    } catch (error) {
      console.error('Erreur fetch patients:', error);
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPatients('', 0, false);
  }, [fetchPatients]);

  const debouncedSearch = useMemo(() => debounce((keyword) => {
    keywordRef.current = keyword;
    setCurrentKeyword(keyword);
    fetchPatients(keyword, 0, false);
  }, 300), [fetchPatients]);

  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

  const handleLoadMore = useCallback(() => {
    if (!hasMoreRef.current || loadingRef.current) return;
    fetchPatients(keywordRef.current, pageRef.current + 1, true);
  }, [fetchPatients]);

  const fetchPatientById = async (id) => {
    try {
      const { data } = await axios.get(`${API_BASE_URL}/api/v1/getPatient/${id}`, {
        withCredentials: true,
      });
      return data;
    } catch (error) {
      console.error('Erreur fetch patient by id:', error);
      return null;
    }
  };

  useEffect(() => {
    const initializeDefaultValue = async () => {
      const patientId = getValues(name);
      if (patientId && !options.find((opt) => opt.value === patientId)) {
        const patient = await fetchPatientById(patientId);
        if (patient && inputRef.current) {
          const externalValue = { value: patient.id, label: patient.fullName, icon: <PatientImage patient={patient} size={24} /> };
          pinnedPatientRef.current = externalValue;
          inputRef.current.setExternalValue(externalValue);
          setOptions((prev) =>
            prev.some((p) => p.value === externalValue.value) ? prev : [externalValue, ...prev]
          );
        }
      }
    };
    initializeDefaultValue();
  }, [getValues, name]);

  const handlePatientCreated = (created) => {
    if (!created) return;
    const opt = { value: created.id, label: created.fullName, icon: <PatientImage patient={created} size={24} /> };
    pinnedPatientRef.current = opt;
    setOptions(prev => [opt, ...prev.filter(p => p.value !== created.id)]);
    inputRef.current?.setExternalValue(opt);
    setShowCreate(false);
  };

  const createFooter = (
    <button
      type="button"
      style={{
        display: 'flex', alignItems: 'center', gap: 8,
        width: '100%', padding: '8px 12px',
        background: '#f0fdf4', border: 'none', borderTop: '1px solid #e5e7eb',
        cursor: 'pointer', color: '#15803d', fontSize: 13, fontWeight: 500,
      }}
      onClick={() => setShowCreate(true)}
    >
      <FontAwesomeIcon icon={faUserPlus} />
      Créer{currentKeyword ? ` "${currentKeyword}"` : ' un nouveau patient'}
    </button>
  );

  return (
    <div className="position-relative">
      <ImprovedSelect
        ref={inputRef}
        name={name}
        multiple={false}
        options={options}
        onChange={handleChange}
        onInputChange={debouncedSearch}
        onLoadMore={handleLoadMore}
        hasMore={hasMore}
        validationRules={validationRules}
        allowCustomValue={allowCustomValue}
        loading={loading}
        className={className}
        noOptionsFooter={createFooter}
        placeholder="Sélectionner un patient..."
      />
      {showCreate && (
        <PatientModal
          show={showCreate}
          onClose={() => setShowCreate(false)}
          patient={(() => {
            const parts = currentKeyword.trim().split(/\s+/).filter(Boolean);
            if (parts.length === 0) return null;
            return { firstName: parts[0], lastName: parts.slice(1).join(' ') || '' };
          })()}
          onSave={handlePatientCreated}
        />
      )}
    </div>
  );
};

export default InputPatient;
